import { GripVertical, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { SortableItem, SortableItemHandle } from '@/components/ui/sortable';
import type { SupplyChainInformation } from '@/types/core/purchase-order/supply-chain-information';

function SupplyChainListItemContent({
  item,
}: {
  item: SupplyChainInformation;
}) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });

  return (
    <div className='min-w-0 flex-1'>
      <p className='font-medium text-declaration-foreground text-sm'>
        {item.actorFlowOrder} -{' '}
        {item.actorName ??
          t('materialCommodityOrigins.cards.supplyChain.unknownActor')}
      </p>
      <p className='text-declaration-muted-foreground text-sm'>
        {t('materialCommodityOrigins.cards.supplyChain.rolePrefix')}{' '}
        {item.actorType.label}
      </p>
    </div>
  );
}

function SupplyChainListItemDeleteButton({
  isPending,
  onOpenDeleteDialog,
}: {
  isPending: boolean;
  onOpenDeleteDialog: () => void;
}) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });

  return (
    <Button
      aria-label={t(
        'materialCommodityOrigins.cards.supplyChain.actions.delete',
      )}
      className='h-auto p-0 text-declaration-foreground shadow-none transition-colors hover:bg-transparent hover:text-declaration-foreground-muted dark:text-declaration-foreground dark:hover:bg-transparent dark:hover:text-declaration-foreground-muted'
      disabled={isPending}
      onClick={onOpenDeleteDialog}
      type='button'
      variant='ghost'
    >
      <Trash2 className='size-[1.35rem]' strokeWidth={2.1} />
    </Button>
  );
}

export function SortableSupplyChainListItem({
  item,
  isPending,
  onOpenDeleteDialog,
}: {
  item: SupplyChainInformation;
  isPending: boolean;
  onOpenDeleteDialog: (id: string) => void;
}) {
  return (
    <SortableItem
      className='flex items-center gap-4 rounded-lg border border-declaration-border bg-white px-4 py-3'
      value={item.id}
    >
      <SortableItemHandle className='text-declaration-muted hover:text-declaration-muted-foreground'>
        <GripVertical className='size-5' />
      </SortableItemHandle>
      <SupplyChainListItemContent item={item} />
      <SupplyChainListItemDeleteButton
        isPending={isPending}
        onOpenDeleteDialog={() => {
          onOpenDeleteDialog(item.id);
        }}
      />
    </SortableItem>
  );
}

export function StaticSupplyChainListItem({
  item,
}: {
  item: SupplyChainInformation;
}) {
  return (
    <div className='flex items-center gap-4 rounded-lg border border-declaration-border bg-white px-4 py-3'>
      <SupplyChainListItemContent item={item} />
    </div>
  );
}
